import { linkOptions, type LinkProps } from "@tanstack/react-router";
import { AdminAuditLog } from "@workspace/admin-domain/src/features/audit-log/schema";
import type { TPermission } from "@workspace/domain/src/utils/permissions";
import { t } from "i18next";
import {
  LucideBanknote,
  LucideBriefcase,
  LucideBuilding2,
  LucidePencil,
  LucidePercent,
  LucidePlane,
  LucidePlus,
  LucideReceipt,
  LucideShieldUser,
  LucideTicket,
  LucideTrash2,
  LucideUser,
  type LucideIcon,
} from "lucide-react";
import * as z from "zod";

export function getActionConfig(action: AdminAuditLog["AuditLog"]["action"]): {
  label: string;
  color: string;
  icon: LucideIcon;
} {
  switch (action) {
    case "CREATE":
      return {
        label: t("auditLog.actions.CREATE"),
        color: "green",
        icon: LucidePlus,
      };
    case "UPDATE":
      return {
        label: t("auditLog.actions.UPDATE"),
        color: "blue",
        icon: LucidePencil,
      };
    case "DELETE":
      return {
        label: t("auditLog.actions.DELETE"),
        color: "red",
        icon: LucideTrash2,
      };
    default:
      return {
        label: action,
        color: "gray",
        icon: LucidePencil,
      };
  }
}

export function getModelConfig(log: AdminAuditLog["AuditLog"]) {
  let icon: LucideIcon;
  let urlInfo: {
    params?: LinkProps;
    permissions?: TPermission[] | TPermission | true;
  } | null;
  let name: string | null;

  switch (log.model) {
    case "TRIP":
      icon = LucidePlane;
      urlInfo = log.resourceId
        ? {
            params: linkOptions({
              to: "/trips/$id",
              params: { id: log.resourceId },
            }),
            permissions: ["trip:get"],
          }
        : null;
      name = t("auditLog.models.TRIP");
      break;

    case "CLIENT":
      icon = LucideUser;
      urlInfo = log.resourceId
        ? {
            params: linkOptions({
              to: "/clients/$id",
              params: { id: log.resourceId },
            }),
            permissions: ["client:get"],
          }
        : null;
      name = t("auditLog.models.CLIENT");
      break;

    case "EMPLOYEE":
      icon = LucideBriefcase;
      urlInfo = log.resourceId
        ? {
            params: linkOptions({
              to: "/employees/$id",
              params: { id: log.resourceId },
            }),
            permissions: ["employee:get"],
          }
        : null;
      name = t("auditLog.models.EMPLOYEE");
      break;

    case "ROLE":
      icon = LucideShieldUser;
      urlInfo = log.resourceId
        ? {
            params: linkOptions({
              to: "/roles",
              search: { search: log.resourceId },
            }),
            permissions: true,
          }
        : null;
      name = t("auditLog.models.ROLE");
      break;

    case "RESERVATION":
      icon = LucideTicket;
      urlInfo = log.resourceId
        ? {
            params: linkOptions({
              to: ".",
              replace: true,
              resetScroll: false,
              search: { reservationId: log.resourceId },
            }),
            permissions: true,
          }
        : null;
      name = t("auditLog.models.RESERVATION");
      break;

    case "AGENCY":
      icon = LucideBuilding2;
      urlInfo = null;
      name = t("auditLog.models.AGENCY");
      break;

    case "PAYMENT":
      icon = LucideReceipt;
      urlInfo = log.resourceId
        ? {
            params: linkOptions({
              to: ".",
              replace: true,
              resetScroll: false,
              search: { paymentId: log.resourceId },
            }),
            permissions: true,
          }
        : null;
      name = t("auditLog.models.PAYMENT");
      break;

    case "DISCOUNT_CODE":
      icon = LucidePercent;
      urlInfo = log.resourceId
        ? {
            params: linkOptions({
              to: "/discount-codes/$id",
              params: { id: log.resourceId },
            }),
            permissions: ["discountCode:get"],
          }
        : null;
      name = t("auditLog.models.DISCOUNT_CODE");
      break;

    case "WITHDRAWAL":
      icon = LucideBanknote;
      urlInfo = {
        params: linkOptions({
          to: "/finances",
          search: { withdrawalId: log.resourceId ?? undefined },
        }),
        permissions: true,
      };
      name = t("auditLog.models.WITHDRAWAL");
      break;

    default:
      icon = LucideReceipt;
      urlInfo = null;
      name = log.model;
      break;
  }

  return { icon, urlInfo, name: log.resourceName ?? name ?? log.model };
}

export const DEFAULT_AUDIT_LOG_PARAMS = {
  page: 1,
  limit: 20,
} satisfies AdminAuditLog["GetAll"];

export const AuditLogFiltersSchema = AdminAuditLog.GetAll.omit({
  page: true,
  limit: true,
});
export type AuditLogFiltersSchema = z.infer<typeof AuditLogFiltersSchema>;
